import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, LogOut, Upload, ArrowLeft, Save, ImageIcon, Loader2, ArrowUp, ArrowDown } from "lucide-react";

function formatError(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [apps, setApps] = useState([]);
  const [settings, setSettings] = useState({ site_title: "", logo_path: null });
  const [savingSettings, setSavingSettings] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "", download_url: "", image_path: null });
  const [imageUploading, setImageUploading] = useState(false);
  const [savingApp, setSavingApp] = useState(false);
  const imageInputRef = useRef(null);

  const loadAll = async () => {
    try {
      const [s, a] = await Promise.all([api.get("/settings"), api.get("/apps")]);
      setSettings(s.data);
      setApps(a.data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  useEffect(() => { loadAll(); }, []);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.path;
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const path = await uploadFile(file);
      setSettings((s) => ({ ...s, logo_path: path }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLogoUploading(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/settings", settings);
      toast.success("Settings saved");
      loadAll();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSavingSettings(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData({ name: "", description: "", download_url: "", image_path: null });
    setDialogOpen(true);
  };

  const openEdit = (app) => {
    setEditing(app);
    setFormData({
      name: app.name,
      description: app.description,
      download_url: app.download_url,
      image_path: app.image_path,
    });
    setDialogOpen(true);
  };

  const handleAppImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    try {
      const path = await uploadFile(file);
      setFormData((f) => ({ ...f, image_path: path }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setImageUploading(false);
    }
  };

  const submitApp = async (e) => {
    e.preventDefault();
    setSavingApp(true);
    try {
      if (editing) {
        await api.put(`/apps/${editing.id}`, formData);
        toast.success("App updated");
      } else {
        await api.post("/apps", formData);
        toast.success("App added");
      }
      setDialogOpen(false);
      loadAll();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSavingApp(false);
    }
  };

  const deleteApp = async (id) => {
    if (!window.confirm("Delete this app?")) return;
    try {
      await api.delete(`/apps/${id}`);
      toast.success("App deleted");
      loadAll();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const moveApp = async (idx, direction) => {
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= apps.length) return;
    const next = [...apps];
    [next[idx], next[target]] = [next[target], next[idx]];
    setApps(next); // optimistic
    try {
      await api.put("/apps/reorder/batch", { ids: next.map((a) => a.id) });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
      loadAll();
    }
  };

  const logoPreview = fileUrl(settings.logo_path);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="pointer-events-none fixed -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#E2FF31]/5 blur-[120px]" />

      <header className="relative z-10 px-8 lg:px-16 py-8 border-b border-white/5 backdrop-blur-2xl bg-black/40 sticky top-0">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="back-home"
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium outline-none transition-all focus-visible:bg-[#E2FF31] focus-visible:text-black focus-visible:scale-105 hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4" />
              Catalog
            </Link>
            <div>
              <span className="text-xs uppercase tracking-[0.3em] text-white/40">Admin</span>
              <h1 className="font-heading text-3xl lg:text-4xl font-black tracking-tighter leading-none">Dashboard</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/50 hidden sm:inline" data-testid="admin-email">{user?.email}</span>
            <Button
              variant="ghost"
              onClick={logout}
              data-testid="logout-btn"
              className="rounded-full border border-white/10 bg-white/5 hover:bg-white/10 h-11 px-5 outline-none focus-visible:bg-[#E2FF31] focus-visible:text-black focus-visible:scale-105"
            >
              <LogOut className="w-4 h-4 mr-2" /> Log out
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 px-8 lg:px-16 py-12 max-w-7xl">
        {/* Site settings */}
        <section className="mb-16" data-testid="site-settings-section">
          <div className="flex items-baseline gap-4 mb-6">
            <span className="text-xs uppercase tracking-[0.3em] text-white/40">01</span>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Site Identity</h2>
          </div>

          <div className="grid md:grid-cols-[220px_1fr] gap-8 rounded-3xl border border-white/10 bg-[#0e0e0e] p-8">
            <div className="flex flex-col items-start gap-4">
              <div className="w-40 h-40 rounded-2xl bg-[#1a1a1a] border border-white/10 overflow-hidden flex items-center justify-center">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" data-testid="settings-logo-preview" />
                ) : (
                  <ImageIcon className="w-10 h-10 text-white/20" />
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                ref={logoInputRef}
                onChange={handleLogoUpload}
                className="hidden"
                data-testid="logo-file-input"
              />
              <Button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                disabled={logoUploading}
                data-testid="upload-logo-btn"
                className="rounded-full bg-white/5 border border-white/10 hover:bg-white/10 h-11 px-5 outline-none focus-visible:bg-[#E2FF31] focus-visible:text-black focus-visible:scale-105"
              >
                {logoUploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {settings.logo_path ? "Replace logo" : "Upload logo"}
              </Button>
            </div>

            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <Label htmlFor="title" className="text-sm uppercase tracking-[0.2em] text-white/50">Site title</Label>
                <Input
                  id="title"
                  data-testid="site-title-input"
                  value={settings.site_title || ""}
                  onChange={(e) => setSettings({ ...settings, site_title: e.target.value })}
                  className="h-14 text-xl bg-[#121212] border-white/10 focus-visible:ring-2 focus-visible:ring-[#E2FF31] focus-visible:border-[#E2FF31]"
                />
              </div>
              <Button
                onClick={saveSettings}
                disabled={savingSettings}
                data-testid="save-settings-btn"
                className="rounded-full bg-[#E2FF31] text-black h-12 px-8 text-base font-bold hover:bg-[#c9e628] w-fit outline-none focus-visible:ring-4 focus-visible:ring-[#E2FF31]/40 focus-visible:scale-105"
              >
                {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save changes
              </Button>
            </div>
          </div>
        </section>

        {/* Apps */}
        <section data-testid="apps-section">
          <div className="flex items-baseline justify-between mb-6 flex-wrap gap-4">
            <div className="flex items-baseline gap-4">
              <span className="text-xs uppercase tracking-[0.3em] text-white/40">02</span>
              <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Apps</h2>
              <span className="text-white/40 text-lg">({apps.length})</span>
            </div>
            <Button
              onClick={openCreate}
              data-testid="add-app-btn"
              className="rounded-full bg-[#E2FF31] text-black h-12 px-6 font-bold hover:bg-[#c9e628] outline-none focus-visible:ring-4 focus-visible:ring-[#E2FF31]/40 focus-visible:scale-105"
            >
              <Plus className="w-5 h-5 mr-2" /> Add app
            </Button>
          </div>

          <div className="rounded-3xl border border-white/10 bg-[#0e0e0e] overflow-hidden">
            {apps.length === 0 ? (
              <div className="p-12 text-center text-white/40" data-testid="apps-empty-state">
                No apps yet. Click <span className="text-white">Add app</span> to publish your first one.
              </div>
            ) : (
              <ul className="divide-y divide-white/5" data-testid="apps-list">
                {apps.map((app, idx) => {
                  const img = fileUrl(app.image_path);
                  return (
                    <li
                      key={app.id}
                      className="flex items-center gap-6 p-5 hover:bg-white/[0.02] transition-colors"
                      data-testid={`app-row-${idx}`}
                    >
                      <div className="w-20 h-20 rounded-xl bg-[#1a1a1a] border border-white/10 overflow-hidden flex-shrink-0 flex items-center justify-center">
                        {img ? (
                          <img src={img} alt={app.name} className="w-full h-full object-cover" />
                        ) : (
                          <ImageIcon className="w-6 h-6 text-white/20" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-heading text-xl font-bold truncate">{app.name}</div>
                        <div className="text-white/50 text-base truncate">{app.description}</div>
                        <div className="text-[#E2FF31]/80 text-sm truncate mt-1 font-mono">{app.download_url}</div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          size="icon"
                          variant="ghost"
                          disabled={idx === 0}
                          onClick={() => moveApp(idx, "up")}
                          data-testid={`move-up-${idx}`}
                          className="w-11 h-11 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed outline-none focus-visible:bg-[#E2FF31] focus-visible:text-black focus-visible:scale-110"
                          title="Move up"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          disabled={idx === apps.length - 1}
                          onClick={() => moveApp(idx, "down")}
                          data-testid={`move-down-${idx}`}
                          className="w-11 h-11 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed outline-none focus-visible:bg-[#E2FF31] focus-visible:text-black focus-visible:scale-110"
                          title="Move down"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEdit(app)}
                          data-testid={`edit-app-${idx}`}
                          className="w-11 h-11 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 outline-none focus-visible:bg-[#E2FF31] focus-visible:text-black focus-visible:scale-110"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => deleteApp(app.id)}
                          data-testid={`delete-app-${idx}`}
                          className="w-11 h-11 rounded-full border border-white/10 bg-white/5 hover:bg-red-500/20 hover:text-red-400 outline-none focus-visible:bg-red-500 focus-visible:text-white focus-visible:scale-110"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      </main>

      {/* App Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0e0e0e] border-white/10 text-white max-w-xl" data-testid="app-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-3xl font-bold tracking-tight">
              {editing ? "Edit app" : "Add app"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitApp} className="flex flex-col gap-5">
            <div className="flex items-center gap-5">
              <div className="w-24 h-24 rounded-xl bg-[#1a1a1a] border border-white/10 overflow-hidden flex items-center justify-center flex-shrink-0">
                {formData.image_path ? (
                  <img src={fileUrl(formData.image_path)} alt="preview" className="w-full h-full object-cover" data-testid="dialog-image-preview" />
                ) : (
                  <ImageIcon className="w-8 h-8 text-white/20" />
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                ref={imageInputRef}
                onChange={handleAppImage}
                className="hidden"
                data-testid="app-image-input"
              />
              <Button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                disabled={imageUploading}
                data-testid="upload-app-image-btn"
                className="rounded-full bg-white/5 border border-white/10 hover:bg-white/10 h-11 outline-none focus-visible:bg-[#E2FF31] focus-visible:text-black focus-visible:scale-105"
              >
                {imageUploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {formData.image_path ? "Replace image" : "Upload image"}
              </Button>
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-sm uppercase tracking-[0.2em] text-white/50">Name</Label>
              <Input
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="h-12 text-base bg-[#121212] border-white/10 focus-visible:ring-2 focus-visible:ring-[#E2FF31] focus-visible:border-[#E2FF31]"
                data-testid="app-name-input"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-sm uppercase tracking-[0.2em] text-white/50">Description</Label>
              <Textarea
                required
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="text-base bg-[#121212] border-white/10 focus-visible:ring-2 focus-visible:ring-[#E2FF31] focus-visible:border-[#E2FF31]"
                data-testid="app-description-input"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-sm uppercase tracking-[0.2em] text-white/50">Download URL</Label>
              <Input
                required
                type="url"
                placeholder="https://…"
                value={formData.download_url}
                onChange={(e) => setFormData({ ...formData, download_url: e.target.value })}
                className="h-12 text-base bg-[#121212] border-white/10 focus-visible:ring-2 focus-visible:ring-[#E2FF31] focus-visible:border-[#E2FF31]"
                data-testid="app-download-url-input"
              />
            </div>

            <DialogFooter className="mt-3 gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDialogOpen(false)}
                className="rounded-full border border-white/10 bg-white/5 hover:bg-white/10 h-11 px-6 outline-none focus-visible:bg-white/20 focus-visible:scale-105"
                data-testid="cancel-app-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={savingApp}
                className="rounded-full bg-[#E2FF31] text-black h-11 px-6 font-bold hover:bg-[#c9e628] outline-none focus-visible:ring-4 focus-visible:ring-[#E2FF31]/40 focus-visible:scale-105"
                data-testid="save-app-btn"
              >
                {savingApp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editing ? "Save changes" : "Add app"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
